import {
  Collapsible as UiCollapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@Pages/pageRuntime/components/ui/Collapsible';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type CollapsibleProps = {
  /**
   * Trigger label shown when collapsed or expanded.
   */
  title: React.ReactNode;

  /**
   * Content revealed inside the panel.
   */
  children: React.ReactNode;

  /**
   * Controlled open state.
   */
  open?: boolean;

  /**
   * Initial uncontrolled open state.
   */
  defaultOpen?: boolean;

  /**
   * Called when the panel opens or closes.
   */
  onOpenChange?: (open: boolean) => void;

  className?: string;
};

/**
 * A single expandable panel for hiding optional details until requested. Use for optional
 * content, not for primary page sections.
 *
 * @example
 * <Sdk.Components.Collapsible title="More details">
 *   <p>Extra context</p>
 * </Sdk.Components.Collapsible>
 */
export function Collapsible({
  title,
  children,
  open,
  defaultOpen,
  onOpenChange,
  className
}: CollapsibleProps) {
  return (
    <UiCollapsible
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      className={className}
    >
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-border bg-card px-4 py-3 text-left font-medium text-card-foreground'
        )}
      >
        {title}
        <span className="text-sm text-muted-foreground">Toggle</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
    </UiCollapsible>
  );
}
